/* eslint-disable react/style-prop-object */
import React, { useState } from "react";
import { Attention, Button, Disclose, Link, Sheet, Steps, Table, Typography } from "@optimizely/axiom";
import { useBulkUpload } from "../../../hooks/useBulkUpload/useBulkUpload";
import { emitToast } from "../../../lib/toaster-utils";
import { UNEXPECTED_ERROR } from "../../../constants/constants";
import { UploadButton } from "../UploadButton/UploadButton";
import {
    ACCEPTED_EXTENSION,
    CSV_TEMPLATE_PATH,
    MAX_FILE_SIZE_MB,
    useCsvFileSelection
} from "../../../hooks/useCsvFileSelection/useCsvFileSelection";
import { BulkProductAccessForm } from "../Access/BulkProductAccess/BulkProductAccessForm";
import { AccessDetailsTable } from "../Access/AccessDetailsTable/AccessDetailsTable";
import { AccessDetailsByProductId } from "../../../domain/AccessDetails";
import { toSelectedUserGroups, UserGroupWithProjectDetails } from "../../../lib/access-helpers";
import { GroupsWithoutProductAccessList } from "../Access/GroupsWithoutProductAccess/GroupsWithoutProductAccessList";
import { useAccessFlowUserFormContext } from "../Access/AccessManagementUserForm/AccessManagementUserFormProvider";

const STEPS = [
    { label: "File", value: 1 },
    { label: "Product Access", value: 2 },
    { label: "Review", value: 3 }
];

interface BulkUploadWithStepsProps {
    isOpen: boolean;
    onClose: () => void;
    organizationId: string;
}

export const BulkUploadWithSteps = ({ isOpen, onClose, organizationId }: BulkUploadWithStepsProps) => {
    if (!isOpen) return null;
    return <BulkUploadWithStepsContent onClose={onClose} organizationId={organizationId} />;
};

BulkUploadWithSteps.displayName = "BulkUploadWithSteps";

const BulkUploadWithStepsContent = ({ onClose, organizationId }: { onClose: () => void; organizationId: string }) => {
    const { uploadUsersCsv } = useBulkUpload({ organizationId });
    const { setUpdateCustomGroupAccess } = useAccessFlowUserFormContext();
    const { selectedFile, errors, setErrors, uploadButtonKey, handleFileSelection, reset } = useCsvFileSelection();
    const [step, setStep] = useState(1);
    const [isUploading, setIsUploading] = useState(false);
    const [accessDetails, setAccessDetails] = useState<AccessDetailsByProductId>({});
    const [groupsWithoutProductAccess, setGroupsWithoutProductAccess] = useState<UserGroupWithProjectDetails[]>([]);

    const handleClose = () => {
        reset();
        setStep(1);
        setIsUploading(false);
        setAccessDetails({});
        setGroupsWithoutProductAccess([]);
        // The custom-group access review flow stores state in the shared
        // AccessManagementUserFormProvider; clear it so closing mid-flow doesn't
        // re-open the confirmation modal next time an access form mounts.
        setUpdateCustomGroupAccess(undefined);
        onClose();
    };

    const handleSubmit = async () => {
        if (!selectedFile) return;

        setIsUploading(true);
        setErrors([]);

        try {
            const userGroupIds = toSelectedUserGroups({
                accessDetailsByProductId: accessDetails,
                groupsWithoutProductAccess
            })
                .map((group) => group.id)
                .filter((id): id is string => !!id);

            await uploadUsersCsv({ file: selectedFile, userGroupIds });
            handleClose();
            emitToast({ message: "Your file was uploaded successfully, and will be processed shortly." });
        } catch (e) {
            const message = e instanceof Error ? e.message : UNEXPECTED_ERROR;
            setErrors(message.split("\n"));
        } finally {
            setIsUploading(false);
        }
    };

    const hasAccess = Object.keys(accessDetails).length > 0 || groupsWithoutProductAccess.length > 0;
    const hasErrors = errors.length > 0;

    const footerButtonList = [
        <Button
            key="cancel"
            onClick={step === 1 ? handleClose : () => setStep((s) => s - 1)}
            isDisabled={isUploading}
            data-testid="bulk-upload-back"
            data-cy="bulk-upload-back"
        >
            {step === 1 ? "Cancel" : "Back"}
        </Button>,
        step === 3 ? (
            <Button
                key="submit"
                style="highlight"
                onClick={handleSubmit}
                isDisabled={!selectedFile || isUploading}
                isLoading={isUploading}
                data-testid="bulk-upload-submit"
                data-cy="bulk-upload-submit"
            >
                Upload
            </Button>
        ) : (
            <Button
                key="continue"
                style="highlight"
                onClick={() => setStep((s) => s + 1)}
                isDisabled={step === 1 && !selectedFile}
                data-testid="bulk-upload-continue"
                data-cy="bulk-upload-continue"
            >
                Continue
            </Button>
        )
    ];

    return (
        <Sheet onClose={handleClose} title="Import Users with CSV" footerButtonList={footerButtonList}>
            <Steps items={STEPS} currentStep={step} userCanEdit={false} className="push-quad--bottom" />

            {hasErrors && (
                <Attention type="bad-news" className="push--bottom" data-testid="bulk-upload-error">
                    {errors.length === 1 ? (
                        errors[0]
                    ) : (
                        <Disclose title={`Upload failed with ${errors.length} errors:`}>
                            <ul className="push--left">
                                {errors.map((err, i) => (
                                    <li key={i}>{err}</li>
                                ))}
                            </ul>
                        </Disclose>
                    )}
                </Attention>
            )}

            <div style={{ display: step === 1 ? "block" : "none" }} data-testid="bulk-step-file">
                <Typography tag="p" type="body" className="push--bottom">
                    Please use{" "}
                    <Link href={CSV_TEMPLATE_PATH} download="users-template.csv">
                        this template
                    </Link>{" "}
                    to format your CSV file (max {MAX_FILE_SIZE_MB}MB and 1,000 users).
                </Typography>

                {selectedFile && (
                    <Typography
                        tag="p"
                        type="body"
                        className="push--bottom"
                        data-testid="bulk-selected-file"
                        data-cy="bulk-selected-file"
                    >
                        Selected file: <strong>{selectedFile.name}</strong>
                    </Typography>
                )}

                <UploadButton
                    key={uploadButtonKey}
                    onSelection={handleFileSelection}
                    extensions={ACCEPTED_EXTENSION}
                    disabled={isUploading}
                    data-testid="bulk-select-file"
                >
                    Select a File
                </UploadButton>
            </div>

            <div style={{ display: step === 2 ? "block" : "none" }} data-testid="bulk-step-access">
                <BulkProductAccessForm
                    organizationId={organizationId}
                    onAccessDetailsChange={setAccessDetails}
                    onGroupsWithoutProductAccessChange={setGroupsWithoutProductAccess}
                />
            </div>

            <div style={{ display: step === 3 ? "block" : "none" }} data-testid="bulk-step-review">
                <div className="push-quad--bottom">
                    <h5 className="push--bottom">You're about to import:</h5>
                    <div className="soft-double--left">
                        <Table density="tight">
                            <Table.THead>
                                <Table.TR>
                                    <Table.TH sorting={{}}>File</Table.TH>
                                </Table.TR>
                            </Table.THead>
                            <Table.TBody>
                                <Table.TR>
                                    <Table.TD data-testid="review-file-name" data-cy="review-file-name">
                                        {selectedFile?.name}
                                    </Table.TD>
                                </Table.TR>
                            </Table.TBody>
                        </Table>
                    </div>
                </div>

                <div className="push-quad--bottom">
                    <h5 className="push--bottom">with the following product access:</h5>
                    {hasAccess ? (
                        <>
                            {Object.keys(accessDetails).length > 0 && (
                                <div data-testid="review-access-table" data-cy="review-access-table">
                                    <AccessDetailsTable accessDetails={accessDetails} />
                                </div>
                            )}
                            {/*
                                Step 2 stays mounted under `display: none`, so its copy of this
                                list is still in the DOM. Distinct selector keeps queries
                                unambiguous.
                            */}
                            <GroupsWithoutProductAccessList
                                groups={groupsWithoutProductAccess}
                                testId="review-groups-without-product-access"
                            />
                        </>
                    ) : (
                        <div className="soft-double--left">
                            <em>No access selected</em>
                        </div>
                    )}
                </div>
            </div>
        </Sheet>
    );
};
